import { useEffect, useState } from 'react';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';
import { picklistService } from '../services/picklistService';

const NONE: ReadonlyMap<number, number> = new Map<number, number>();

interface Settled {
  key: string;
  byBin: ReadonlyMap<number, number>;
  error: string | null;
}

/**
 * What other open picklists hold of each bin of ONE lot or untracked item (14ykpqtv7nd) — `useSerialCommitments`'
 * counterpart for stock that has no serial to name. One database read, fired once the stock rows are in hand; no
 * NetSuite call.
 *
 * `isLoading` is true from the first render that has an item to ask about, so the dialog keeps its skeleton until the
 * answer lands and never offers a held bin's units for a moment. A failed read is reported, not swallowed: the rows
 * then show NetSuite's raw quantities, and the server still refuses a claim beyond what is free on save.
 *
 * @param excludePicklistId Add Items only: the picklist being added to, whose own slots are not another's.
 */
export function useBinHolds(enabled: boolean, netsuiteItemId: number, excludePicklistId?: number) {
  const key = enabled && netsuiteItemId > 0 ? `${excludePicklistId ?? 0}:${netsuiteItemId}` : '';
  const [settled, setSettled] = useState<Settled | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    const [exclude, itemId] = key.split(':').map(Number);
    picklistService
      .getBinHolds({ netsuiteItemIds: [itemId], excludePicklistId: exclude > 0 ? exclude : null })
      .then((rows) => {
        if (cancelled) return;
        const byBin = new Map<number, number>();
        for (const row of rows) {
          if (row.netsuiteItemId === itemId && row.pendingQuantity > 0) byBin.set(row.binId, row.pendingQuantity);
        }
        setSettled({ key, byBin, error: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setSettled({
          key,
          byBin: NONE,
          error: getApiErrorMessage(error, 'Couldn’t check which bins are held by other picklists.'),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [key, attempt]);

  const current = settled !== null && settled.key === key ? settled : null;
  return {
    isLoading: key !== '' && current === null,
    byBin: current?.byBin ?? NONE,
    error: current?.error ?? null,
    retry: () => {
      setSettled(null);
      setAttempt((n) => n + 1);
    },
  };
}
